<template>
  <div>
    <h2 class="vb-heading h5 mb-0">Pernotto e servizi</h2>
    <div
      v-for="(hotel, hotel_index) in hotels_info"
      :key="`${hotel.name}:${hotel_index}`"
    >
      <div
        class="vb-hotels vb-hotels--boxed"
        :class="{ 'vb-hotels--media': hotel.show_media }"
      >
        <b-row>
          <b-col
            v-if="hotel.show_media"
            class="vb-hotels__column--first"
            cols="12"
            lg="3"
          >
            <div
              class="vb-hotels__image"
              :class="{ 'vb-hotels__image--clickable': modal_enabled }"
              :style="hotel.media_style"
              @click="open_modal(hotel_index)"
            >
              <span class="vb-hotels__image-corner"></span>
            </div>
          </b-col>
          <b-col
            class="vb-hotels__column--second"
            cols="12"
            :lg="hotel.show_media ? 9 : 12"
          >
            <div class="vb-hotels__box">
              <div class="vb-hotels__item vb-hotels__item--head">
                <vb-icon
                  class="vb-hotels__item-icon"
                  :name="'hotel'"
                  :size="20"
                  :color="sass('primary')"
                />
                <span class="vb-hotels__item-text">{{ hotel.name }}</span>
              </div>
              <template v-if="hotel.show_media && hotel.abstract">
                <hr class="vb-hotels__divider" />
                <div class="vb-hotels__editorial">
                  <p
                    class="vb-hotels__abstract"
                    :class="{ 'vb-hotels__abstract--clamped': modal_enabled }"
                    v-html="hotel.abstract"
                  ></p>
                  <button
                    v-if="modal_enabled"
                    type="button"
                    class="vb-hotels__readmore"
                    @click="open_modal(hotel_index)"
                  >
                    leggi tutto
                  </button>
                </div>
              </template>
              <hr class="vb-hotels__divider" />
              <div class="vb-hotels__wrap">
                <div class="vb-hotels__item">
                  <span>IN {{ reverse_date(hotel.checkIn) }}</span>
                  <vb-icon
                    class="vb-hotels__item-icon vb-hotels__item-icon--check"
                    :name="'chevron-right'"
                    :size="16"
                    :color="sass('primary')"
                  />
                  <span>OUT {{ reverse_date(hotel.checkOut) }}</span>
                </div>
                <div class="vb-hotels__item">
                  <open-in-maps
                    v-if="
                      hotel.location &&
                        get_config(
                          'guiSettings.customizations.showOpenInMap',
                          true
                        )
                    "
                    :location="{
                      ...hotel.location,
                      name: hotel.name,
                      city: hotel.locationLabel,
                    }"
                    :only_use_coordinates="
                      get_config(
                        'guiSettings.customizations.openMapWithOnlyCoordinates',
                        false
                      )
                    "
                  />
                </div>
                <div class="vb-hotels__item">
                  <vb-icon
                    class="vb-hotels__item-icon"
                    :name="'bed'"
                    :size="18"
                    :color="sass('primary')"
                  />
                  <span>{{ room_parsed.label }}</span>
                </div>
                <div class="vb-hotels__item">
                  <vb-icon
                    class="vb-hotels__item-icon"
                    :name="'cutlery'"
                    :size="18"
                    :color="sass('primary')"
                  />
                  <span>{{ room_parsed.treatment.label }}</span>
                </div>
                <div v-if="flight_and_stay" class="vb-hotels__item">
                  <vb-icon
                    class="vb-hotels__item-icon"
                    :name="'bus'"
                    :size="18"
                    :color="sass('primary')"
                  />
                  <span class="text-uppercase">Trasferimenti *</span>
                </div>
                <div v-if="flight_and_stay" class="vb-hotels__item">
                  <vb-icon
                    class="vb-hotels__item-icon"
                    :name="'shield'"
                    :size="18"
                    :color="sass('primary')"
                  />
                  <span class="text-uppercase">Assicurazione</span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
        <hotel-modal
          v-if="modal_enabled"
          :modal_id="modal_id(hotel_index)"
          :hotel="hotel"
          :room="room_parsed"
          :flight_and_stay="flight_and_stay"
          :images="carousel_images"
        />
        <div v-if="visible_supplements.length" class="vb-hotels__service">
          <accordion-block
            :title="'Servizi inclusi nel pacchetto'"
            :collapse_id="`servizi-inclusi-${hotel_index}`"
            :heading="false"
          >
            <ul>
              <li
                v-for="(suppl, suppl_index) in visible_supplements"
                :key="`${suppl.name}:${suppl_index}`"
              >
                {{ suppl.name.toLowerCase() }}
              </li>
            </ul>
          </accordion-block>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import { get_config, get_object_field } from '@/utilities';
  import { reverse_date } from '@utilities/commons';
  import OpenInMaps from '@/components/atoms/open-in-maps.vue';
  import AccordionBlock from '@alpitour/components/blocks/detail/accordion-block.vue';
  import HotelModal from '@alpitour/components/blocks/hotel-modal.vue';
  export default {
    name: 'hotels',

    components: {
      'open-in-maps': OpenInMaps,
      'accordion-block': AccordionBlock,
      'hotel-modal': HotelModal,
    },

    props: {
      hotels: {
        type: Array,
        required: true,
      },
      supplements: {
        type: Array,
        default: () => [],
      },
      flight_and_stay: {
        type: Boolean,
        default: false,
      },
      statics_fallback: {
        type: Boolean,
        default: true,
      },
    },

    computed: {
      ...mapState('liveat', ['statics', 'pid', 'selected_room']),
      editorial_statics() {
        const statics = this.statics || {};
        const payload_pid = get_object_field(this.selected_room, 'quote_payload.pid', '');
        return statics[this.pid] || statics[payload_pid] || {};
      },
      modal_enabled() {
        const route_name = (this.$route || {}).name;
        return !['confirm', 'prenotazioni'].includes(route_name);
      },
      carousel_images() {
        const { carousel_images = [] } = this.editorial_statics;
        if (carousel_images.length) return carousel_images;
        const cover = this.editorial_statics.urlThumbProdotto || '';
        return cover ? [{ url: cover, thumbnail: cover }] : [];
      },
      hotels_info() {
        return this.hotels.map((hotel, hotel_index) => {
          const image = hotel.urlFoto
            || (this.statics_fallback ? this.editorial_statics.urlThumbProdotto || '' : '');
          const abstract = hotel.descBreve
            || (this.statics_fallback ? this.editorial_statics.abstractProdotto || '' : '');
          return {
            ...hotel,
            name: hotel.name || hotel.hotel_name || '',
            checkIn: hotel.checkIn || hotel.checkin || '',
            checkOut: hotel.checkOut || hotel.checkout || '',
            locationLabel: this.location_label(hotel),
            show_media: hotel_index === 0,
            abstract,
            media_style: {
              'background-image': `${image ? `url(${image}), ` : ''}url('/assets/images/common/statics/room_placeholder.png')`,
            },
          };
        });
      },
      room_parsed() {
        const first = this.hotels && this.hotels[0];
        if (!first) return { label: '', treatment: { label: '' } };

        if (first.offers) {
          let result = { label: '', treatment: { label: '' } };
          this.hotels.forEach((hotel) => {
            hotel.offers.forEach((offer) => {
              offer.rooms.forEach((room) => {
                room.boards.forEach((board) => {
                  if (board.selected) {
                    result = {
                      id: room.id,
                      label: room.name,
                      price:
                        parseFloat(room.priceDescriptor.netPrice) +
                        parseFloat(board.totalPrice),
                      treatment: {
                        id: board.id,
                        label: board.description,
                        totalPrice: board.totalPrice,
                      },
                    };
                  }
                });
              });
            });
          });
          return result;
        }

        // Flat sololand shape — room/board can be plain strings or nested objects
        const room = first.room || {};
        if (typeof room === 'string') {
          return {
            id: room,
            label: room,
            price: 0,
            treatment: {
              id: first.board || '',
              label: first.board || '',
              totalPrice: '0',
            },
          };
        }
        const rate = room.rate || {};
        const pricing = rate.pricing || {};
        return {
          id: room.name || '',
          label: room.name || '',
          price: pricing.total || 0,
          treatment: {
            id: rate.description || '',
            label: rate.description || '',
            totalPrice: '0',
          },
        };
      },
      visible_supplements() {
        return this.supplements.filter((supplement) => supplement.visible);
      },
    },
    created() {
      this.reverse_date = reverse_date;
    },
    methods: {
      get_config,
      modal_id(hotel_index) {
        return `hotel-modal-${this._uid}-${hotel_index}`;
      },
      open_modal(hotel_index) {
        if (!this.modal_enabled) return;
        this.$bvModal.show(this.modal_id(hotel_index));
      },
      location_label(hotel) {
        const { city, country } = hotel.location || {};
        return [city, country].filter(Boolean).join(', ').toLowerCase();
      },
    },
  };
</script>

<style lang="scss">
  .vb-hotels {
    padding-top: $grid-gutter-width/2;

    &__wrap {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }

    &__item {
      display: flex;
      align-items: center;
      margin-right: $grid-gutter-width/2;
      margin-bottom: $grid-gutter-width/4;

      &-icon {
        margin-right: $grid-gutter-width/5;

        &--check {
          margin-left: $grid-gutter-width/5;
        }
      }
    }

    &__service {
      margin-top: $grid-gutter-width;
      width: 100%;

      @include media-breakpoint-up(lg) {
        width: 40%;
      }

      .accordion-block.accordion-block {
        background-color: transparent;
        border-radius: 0;

        .accordion-block__header,
        .accordion-block__body {
          padding-left: 0;
          padding-right: 0;
        }
      }

      li {
        list-style: circle;
      }
    }

    &--boxed {
      position: relative;

      .vb-hotels__column--first,
      .vb-hotels__column--second {
        display: flex;
      }

      @include media-breakpoint-up(lg) {
        .vb-hotels__column--first {
          max-width: 214px + $grid-gutter-width;
        }

        .vb-hotels__column--second {
          flex: 1 1 0%;
          max-width: none;
        }
      }

      .vb-hotels__image {
        @include clipped-media-image($border-radius-lg * 2);
        flex: 1 1 auto;

        &-corner {
          @include clipped-media-corner($border-radius-lg * 2, $white);
        }
      }

      .vb-hotels__image--clickable {
        cursor: pointer;
      }

      .vb-hotels__editorial {
        position: relative;
      }

      .vb-hotels__readmore {
        position: absolute;
        right: 0;
        bottom: 0;
        padding: 0 0 0 $grid-gutter-width/3;
        border: 0;
        background-color: $white;
        color: var(--info);
        font-size: $font-size-sm;
        text-decoration: underline;
        cursor: pointer;
      }

      .vb-hotels__box {
        flex: 1 1 auto;
        background-color: $white;
        border: 1px solid $gray-200;
        border-radius: $border-radius-lg * 2;
        padding: $spacer;
      }

      .vb-hotels__item--head {
        margin-bottom: 0;
      }

      .vb-hotels__item-text {
        font-weight: $vb-font-weight-semibold;
      }

      .vb-hotels__abstract {
        font-size: $font-size-sm;
        margin-bottom: 0;

        &--clamped {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          box-orient: vertical;
          line-clamp: 2;
          overflow: hidden;
        }

        b,
        strong {
          font-weight: $vb-font-weight-semibold;
        }
      }

      .vb-hotels__divider {
        border-top: 1px solid $gray-200;
        margin: 12px 0;
      }
    }

    &--media {
      .vb-hotels__box {
        border-radius: 0 ($border-radius-lg * 2) ($border-radius-lg * 2);
        margin-top: -40px;

        @include media-breakpoint-up(lg) {
          margin-top: 0;
          margin-left: -40px;
        }
      }

      .vb-hotels__service {
        @include media-breakpoint-up(lg) {
          margin-left: 214px + $grid-gutter-width;
          width: auto;
        }
      }
    }
  }
</style>
